import { MAX_HHMM_HOURS_DIGITS } from '../../constants/hhmmHoursLimits';

/** Units available on component / assembly Time Since Record rows */
export const TSR_TIME_UNITS = Object.freeze([
  'Hours',
  'Years',
  'Calendar Days',
  'Cycles',
  'Landings',
  'Hobbs Meter',
  'Hobbs Meter AC'
]);

export function isHobbsMeterTsrUnit(unit) {
  const u = String(unit || '').trim();
  return u === 'Hobbs Meter' || u === 'Hobbs Meter AC';
}

export function isDecimalNumericTsrUnit(unit) {
  const u = String(unit || '').trim();
  return u === 'Cycles' || isHobbsMeterTsrUnit(u);
}

// Pure helper functions for ComponentForm

// Time conversion utilities
export const timeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(':')) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

export const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

// Years/Months calculation utilities
export const calculateYearsFromDates = (startDate, asOnDate) => {
  if (!startDate || !asOnDate) return '0.00';
  const start = new Date(startDate);
  const end = new Date(asOnDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '0.00';
  
  // Calculate years and months difference
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  
  // Adjust if end month is before start month
  if (months < 0) {
    years--;
    months += 12;
  }
  
  // Adjust if end day is before start day (within the same month)
  // If end day is earlier in the month, we don't count that month yet
  if (end.getDate() < start.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }
  
  // Ensure months is 0-11 (0-11 months, not 0-12)
  // 12 months would be 1 year, so max is 11
  if (months >= 12) {
    years += Math.floor(months / 12);
    months = months % 12;
  }
  
  // Ensure years doesn't exceed 99 (2 digits max)
  if (years > 99) {
    years = 99;
  }
  
  // Format as YY.MM (years.months)
  const monthsStr = String(months).padStart(2, '0');
  return `${years}.${monthsStr}`;
};

export const parseYearsMonthsToDecimal = (valueStr) => {
  if (!valueStr) return 0;
  // Handle format YY.MM where MM is months (0-12)
  if (valueStr.includes('.')) {
    const [years, months] = valueStr.split('.');
    const yearsNum = parseInt(years) || 0;
    const monthsNum = parseInt(months) || 0;
    // Convert to decimal: years + (months / 12)
    return yearsNum + (monthsNum / 12);
  }
  const parsed = parseFloat(valueStr);
  return isNaN(parsed) ? 0 : parsed;
};

export const formatDecimalToYearsMonths = (decimalValue) => {
  if (isNaN(decimalValue) || decimalValue < 0) return '0.00';
  const years = Math.floor(decimalValue);
  const months = Math.round((decimalValue - years) * 12);
  // Ensure months doesn't exceed 12
  const finalMonths = months >= 12 ? 11 : months;
  const monthsStr = String(finalMonths).padStart(2, '0');
  return `${years}.${finalMonths}`;
};

export const parseNumericValue = (valueStr) => {
  if (!valueStr) return 0;
  // For Years format, use the new parser
  return parseYearsMonthsToDecimal(valueStr);
};

// Validate Calendar Days input (max 5 digits, no decimals)
export const validateCalendarDaysInput = (value) => {
  if (!value) return '';
  const cleaned = value.replace(/[^\d]/g, '');
  return cleaned.length > 5 ? cleaned.substring(0, 5) : cleaned;
};

// Validate Cycles input (numeric with up to 2 decimal places)
export const validateCyclesInput = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  const match = trimmed.match(/^\d*\.?\d{0,2}/);
  if (!match || !match[0]) return '';
  if (match[0] === '.') return '';
  return match[0];
};

// Parse Cycles value (allows decimals, round to 2 decimal places)
export const parseCyclesValue = (valueStr) => {
  if (!valueStr) return 0;
  const parsed = parseFloat(String(valueStr).replace(/,/g, ''));
  return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
};

// Parse integer numeric value (Calendar Days, Landings - no decimals)
export const parseIntegerNumericValue = (valueStr) => {
  if (!valueStr) return 0;
  const parsed = parseInt(String(valueStr).replace(/,/g, ''), 10);
  return isNaN(parsed) ? 0 : parsed;
};

/** Average days per year for Years ↔ Calendar Days conversion (matches common aviation tooling). */
const DAYS_PER_YEAR = 365.25;

/**
 * When life limit (frequency) and elapsed use different TSR units, compute remaining
 * where a conversion is defined (mixed Years+Calendar Days returns remaining calendar days).
 * @returns {{ value: string, unit: string } | null} null if pair is not supported
 */
export const computeRemainingWhenUnitsDiffer = (limitValue, limitUnit, elapsedValue, elapsedUnit) => {
  const lu = String(limitUnit || '').trim();
  const eu = String(elapsedUnit || '').trim();
  if (!lu || !eu || lu === eu) return null;

  if (lu === 'Years' && eu === 'Calendar Days') {
    const limitDec = parseYearsMonthsToDecimal(String(limitValue ?? '0.00'));
    const elapsedDays = parseIntegerNumericValue(String(elapsedValue ?? '0'));
    // Remaining in calendar days directly — do not round-trip through YY.MM (that loses ~0.03 yr ≈ 10 days here).
    const limitDays = limitDec * DAYS_PER_YEAR;
    const remDays = Math.max(0, Math.round(limitDays - elapsedDays));
    return { value: String(remDays), unit: 'Calendar Days' };
  }
  if (lu === 'Calendar Days' && eu === 'Years') {
    const limitDays = parseIntegerNumericValue(String(limitValue ?? '0'));
    const elapsedDec = parseYearsMonthsToDecimal(String(elapsedValue ?? '0.00'));
    const elapsedDays = Math.round(elapsedDec * DAYS_PER_YEAR);
    const rem = Math.max(0, limitDays - elapsedDays);
    return { value: String(Math.floor(rem)), unit: 'Calendar Days' };
  }

  return null;
};

/**
 * Convert a canonical remaining (computed in limit unit for mixed Years/CD rows)
 * into the display unit the user chose for the Remaining column.
 */
export const convertRemainingCanonicalToDisplay = (canonicalValue, canonicalUnit, displayUnit) => {
  const cu = String(canonicalUnit || '').trim();
  const du = String(displayUnit || '').trim();
  if (!cu || !du || cu === du) {
    return { value: String(canonicalValue ?? ''), unit: du || cu };
  }
  if (cu === 'Years' && du === 'Calendar Days') {
    const dec = parseYearsMonthsToDecimal(String(canonicalValue ?? '0.00'));
    const days = Math.round(dec * DAYS_PER_YEAR);
    return { value: String(Math.max(0, days)), unit: 'Calendar Days' };
  }
  if (cu === 'Calendar Days' && du === 'Years') {
    const days = parseIntegerNumericValue(String(canonicalValue ?? '0'));
    const dec = Math.max(0, days / DAYS_PER_YEAR);
    return { value: formatDecimalToYearsMonths(dec), unit: 'Years' };
  }
  return { value: String(canonicalValue ?? ''), unit: du || cu };
};

/** True when limit and elapsed are Years vs Calendar Days (mixed format with conversion). */
export const isMixedYearsCalendarLimitElapsed = (limitUnit, elapsedUnit) => {
  const lu = String(limitUnit || '').trim();
  const eu = String(elapsedUnit || '').trim();
  return (
    (lu === 'Years' && eu === 'Calendar Days') || (lu === 'Calendar Days' && eu === 'Years')
  );
};

/** Years or Calendar Days elapsed — airframe/parent installation snapshots are not used (dates drive elapsed). */
export const elapsedIsCalendarBased = (elapsedUnit) => {
  const u = String(elapsedUnit || '').trim();
  return u === 'Years' || u === 'Calendar Days';
};

/**
 * Canonical remaining = limit − elapsed (mixed Years+Calendar Days: canonical is Calendar Days;
 * mixed Calendar Days+Years: canonical is Calendar Days from computeRemainingWhenUnitsDiffer).
 */
export const computeRemainingSnapshot = (record) => {
  if (!record) return null;
  const limitUnit = record.limit?.unit || 'Hours';
  const elapsedUnit = record.elapsedTime?.unit || 'Hours';

  if (limitUnit === elapsedUnit) {
    const u = limitUnit;
    if (u === 'Hours') {
      const elapsedTimeStr = record.elapsedTime?.value || '00:00';
      const limitTimeStr = record.limit?.value || '00:00';
      const elapsedMinutes = timeToMinutes(elapsedTimeStr);
      const limitMinutes = timeToMinutes(limitTimeStr);
      const remainingMinutes = Math.max(0, limitMinutes - elapsedMinutes);
      return { value: minutesToTime(remainingMinutes), unit: u };
    }
    if (u === 'Years') {
      const elapsedValue = parseYearsMonthsToDecimal(record.elapsedTime?.value || '0.00');
      const limitValue = parseYearsMonthsToDecimal(record.limit?.value || '0.00');
      const remainingValue = Math.max(0, limitValue - elapsedValue);
      return { value: formatDecimalToYearsMonths(remainingValue), unit: u };
    }
    if (isDecimalNumericTsrUnit(u)) {
      const elapsedValue = parseCyclesValue(record.elapsedTime?.value || '0');
      const limitValue = parseCyclesValue(record.limit?.value || '0');
      const remainingValue = Math.max(0, limitValue - elapsedValue);
      return { value: (Math.round(remainingValue * 100) / 100).toString(), unit: u };
    }
    if (u === 'Calendar Days' || u === 'Landings') {
      const elapsedValue = parseIntegerNumericValue(record.elapsedTime?.value || '0');
      const limitValue = parseIntegerNumericValue(record.limit?.value || '0');
      const remainingValue = Math.max(0, limitValue - elapsedValue);
      return { value: Math.floor(remainingValue).toString(), unit: u };
    }
    return null;
  }
  return computeRemainingWhenUnitsDiffer(record.limit?.value, limitUnit, record.elapsedTime?.value, elapsedUnit);
};

// Helper function to calculate End Date from As On Date + Remaining Time
export const calculateEndDate = (asOnDate, remainingTime) => {
  if (!asOnDate || !remainingTime?.value || !remainingTime?.unit) return null;
  
  const baseDate = new Date(asOnDate);
  if (isNaN(baseDate.getTime())) return null;
  
  const unit = remainingTime.unit;
  const value = remainingTime.value;
  
  if (unit === 'Hours') {
    // Parse HH:MM format
    if (!value || !value.includes(':')) return null;
    const [hours, minutes] = value.split(':').map(Number);
    const totalMinutes = (hours || 0) * 60 + (minutes || 0);
    const endDate = new Date(baseDate);
    endDate.setMinutes(endDate.getMinutes() + totalMinutes);
    return endDate;
  } else if (unit === 'Years') {
    // Parse YY.MM format
    if (!value || !value.includes('.')) return null;
    const [years, months] = value.split('.').map(Number);
    const endDate = new Date(baseDate);
    endDate.setFullYear(endDate.getFullYear() + (years || 0));
    endDate.setMonth(endDate.getMonth() + (months || 0));
    return endDate;
  } else if (unit === 'Calendar Days') {
    // Parse integer days
    const days = parseInt(value) || 0;
    const endDate = new Date(baseDate);
    endDate.setDate(endDate.getDate() + days);
    return endDate;
  }
  
  return null;
};

/**
 * For TSR rows in Years or Calendar Days: show calendar range from (start or as-on) to projected due
 * (As On Date + remaining in calendar form). Returns null when not applicable or data is insufficient.
 */
export const formatTsrCalendarDueRangeDisplay = (record) => {
  if (!record) return null;
  const unit = String(record.remainingTime?.unit || record.elapsedTime?.unit || '').trim();
  if (unit !== 'Years' && unit !== 'Calendar Days') return null;
  if (!record.asOnDate) return null;
  const remVal = record.remainingTime?.value;
  if (remVal == null || String(remVal).trim() === '') return null;
  const end = calculateEndDate(record.asOnDate, record.remainingTime);
  if (!end || isNaN(end.getTime())) return null;
  const fmt = (raw) => {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const fromSource = record.startDate || record.asOnDate;
  const fromStr = fmt(fromSource);
  const toStr = fmt(end);
  if (!toStr) return null;
  if (!fromStr) return `— → ${toStr}`;
  return `${fromStr} → ${toStr}`;
};

/** Calendar days from today (local midnight) until projected due (local midnight); negative if overdue. */
const CALENDAR_DUE_URGENT_DAYS = 7;

const startOfLocalDay = (d) => {
  const x = new Date(d);
  if (isNaN(x.getTime())) return null;
  x.setHours(0, 0, 0, 0);
  return x;
};

/**
 * Whole calendar days from start of local today until start of local due date (negative if overdue).
 * Same applicability as formatTsrCalendarDueRangeDisplay.
 */
export const getCalendarDueDaysRemaining = (record) => {
  if (!record) return null;
  const unit = String(record.remainingTime?.unit || record.elapsedTime?.unit || '').trim();
  if (unit !== 'Years' && unit !== 'Calendar Days') return null;
  if (!record.asOnDate) return null;
  const remVal = record.remainingTime?.value;
  if (remVal == null || String(remVal).trim() === '') return null;
  const end = calculateEndDate(record.asOnDate, record.remainingTime);
  if (!end || isNaN(end.getTime())) return null;
  const t0 = startOfLocalDay(new Date());
  const t1 = startOfLocalDay(end);
  if (!t0 || !t1) return null;
  return Math.round((t1.getTime() - t0.getTime()) / 86400000);
};

/** MUI sx color: urgent (≤7 days or overdue) vs not. */
export const getCalendarDueRangeMuiColor = (record) => {
  const days = getCalendarDueDaysRemaining(record);
  if (days == null) return 'text.secondary';
  return days <= CALENDAR_DUE_URGENT_DAYS ? 'error.main' : 'success.main';
};

// Helper function to generate explanation text for End Date calculation
export const getEndDateCalculationExplanation = (asOnDate, remainingTime) => {
  if (!asOnDate || !remainingTime?.value || !remainingTime?.unit) {
    return 'End Date will be calculated when As On Date and Remaining Time are provided.';
  }
  
  const baseDate = new Date(asOnDate);
  if (isNaN(baseDate.getTime())) {
    return 'Invalid As On Date.';
  }
  
  const unit = remainingTime.unit;
  const value = remainingTime.value;
  
  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const asOnDateFormatted = formatDate(baseDate);
  
  if (unit === 'Hours') {
    if (!value || !value.includes(':')) {
      return 'Invalid Hours format. Expected HH:MM.';
    }
    const [hours, minutes] = value.split(':').map(Number);
    const totalMinutes = (hours || 0) * 60 + (minutes || 0);
    const endDate = calculateEndDate(asOnDate, remainingTime);
    
    if (!endDate) return 'Unable to calculate End Date.';
    
    const endDateFormatted = formatDate(endDate);
    const hoursText = hours > 0 ? `${hours} hour${hours !== 1 ? 's' : ''}` : '';
    const minutesText = minutes > 0 ? `${minutes} minute${minutes !== 1 ? 's' : ''}` : '';
    const timeText = [hoursText, minutesText].filter(Boolean).join(' and ') || '0 minutes';
    
    return `As On Date (${asOnDateFormatted}) + Remaining Time (${timeText}) = ${endDateFormatted}`;
  } else if (unit === 'Years') {
    if (!value || !value.includes('.')) {
      return 'Invalid Years format. Expected YY.MM.';
    }
    const [years, months] = value.split('.').map(Number);
    const endDate = calculateEndDate(asOnDate, remainingTime);
    
    if (!endDate) return 'Unable to calculate End Date.';
    
    const endDateFormatted = formatDate(endDate);
    const yearsText = years > 0 ? `${years} year${years !== 1 ? 's' : ''}` : '';
    const monthsText = months > 0 ? `${months} month${months !== 1 ? 's' : ''}` : '';
    const timeText = [yearsText, monthsText].filter(Boolean).join(' and ') || '0 months';
    
    return `As On Date (${asOnDateFormatted}) + Remaining Time (${timeText}) = ${endDateFormatted}`;
  } else if (unit === 'Calendar Days') {
    const days = parseInt(value) || 0;
    const endDate = calculateEndDate(asOnDate, remainingTime);
    
    if (!endDate) return 'Unable to calculate End Date.';
    
    const endDateFormatted = formatDate(endDate);
    const daysText = `${days} day${days !== 1 ? 's' : ''}`;
    
    return `As On Date (${asOnDateFormatted}) + Remaining Time (${daysText}) = ${endDateFormatted}`;
  }
  
  return 'Unable to calculate End Date.';
};

// Component code generation utilities
export const getAssemblyTypeCode = (typeName) => {
  if (!typeName) return '';
  // Convert to uppercase and take first 3-4 letters
  // Handle common cases: "Engine" -> "ENG", "Airframe" -> "AIR", "Landing Gear" -> "LND"
  const upper = typeName.toUpperCase().trim();
  // Remove spaces and take first 3-4 meaningful characters
  const withoutSpaces = upper.replace(/\s+/g, '');
  if (withoutSpaces.length <= 4) return withoutSpaces;
  // For longer names, take first 3-4 characters
  return withoutSpaces.substring(0, 4);
};

export const extractSequenceFromCode = (code) => {
  if (!code) return '001';
  const parts = code.split('-');
  if (parts.length === 3 && parts[2]) {
    return parts[2].trim();
  }
  return '001';
};

export const generateComponentCode = (assemblyId, position, sequence, assemblies) => {
  if (!assemblyId || !position) return '';
  
  const selectedAssembly = assemblies.find(a => {
    const assemblyIdStr = a._id ? String(a._id) : null;
    const valueStr = String(assemblyId);
    return assemblyIdStr === valueStr;
  });
  
  if (!selectedAssembly) return '';
  
  const typeCode = getAssemblyTypeCode(selectedAssembly.typeName || selectedAssembly.type?.name || '');
  if (!typeCode) return '';
  
  // Format: {ASSEMBLY_TYPE}-{POSITION}-{SEQUENCE}
  const positionUpper = String(position).toUpperCase().trim();
  // Keep sequence as-is (user can edit it), but ensure it's not empty
  const sequenceStr = String(sequence).trim() || '001';
  
  return `${typeCode}-${positionUpper}-${sequenceStr}`;
};

/**
 * Generate a stable, unique display code for an assembly (no duplicates across aircraft).
 * Format with position: {TYPE_CODE}-{POSITION}-{ID_SUFFIX}
 * Format without position: {TYPE_CODE}-{ID_SUFFIX}
 * ID_SUFFIX is last 6 chars of assembly._id for global uniqueness.
 */
export const generateAssemblyCode = (assembly) => {
  if (!assembly) return '';
  const typeName = assembly.type?.name || assembly.typeName || '';
  const typeCode = getAssemblyTypeCode(typeName);
  if (!typeCode) return '';
  const idStr = assembly._id ? String(assembly._id) : '';
  const idSuffix = idStr.length >= 6 ? idStr.slice(-6).toLowerCase() : idStr.toLowerCase();
  if (!idSuffix) return typeCode;
  const position = String(assembly.position || '').toUpperCase().trim();
  if (position) return `${typeCode}-${position}-${idSuffix}`;
  return `${typeCode}-${idSuffix}`;
};

// String matching utilities
export const normalizeForMatching = (str) => {
  if (!str) return '';
  return str.trim()
    .toLowerCase()
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .replace(/[^\w\s]/g, '') // Remove special characters except word chars and spaces
    .trim();
};

export const stringsMatch = (str1, str2) => {
  const normalized1 = normalizeForMatching(str1);
  const normalized2 = normalizeForMatching(str2);
  
  // Exact match
  if (normalized1 === normalized2) return true;
  
  // Check if one contains the other (for cases like "Gulfstream" matching "Gulfstream Aerospace")
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    // Only consider it a match if the shorter string is at least 3 characters
    const shorter = normalized1.length < normalized2.length ? normalized1 : normalized2;
    return shorter.length >= 3;
  }
  
  return false;
};

// Helper function to validate Hours input (HH:MM format)
// Returns: { value: string, error: string }
export const validateHoursInput = (value) => {
  if (!value || value.trim() === '') {
    return { value: '', error: '' };
  }
  
  const trimmed = value.trim();
  
  // Check if colon is present
  const colonIndex = trimmed.indexOf(':');
  
  if (colonIndex === -1) {
    const digitsOnly = trimmed.replace(/[^\d]/g, '');
    if (digitsOnly.length > MAX_HHMM_HOURS_DIGITS) {
      return {
        value: digitsOnly.substring(0, MAX_HHMM_HOURS_DIGITS),
        error: `Hours cannot exceed ${MAX_HHMM_HOURS_DIGITS} digits`
      };
    }
    if (trimmed !== digitsOnly && digitsOnly.length > 0) {
      return {
        value: digitsOnly,
        error: 'Only digits allowed before colon'
      };
    }
    return { value: digitsOnly, error: '' };
  }
  
  // Colon is present - validate both parts
  const hoursPart = trimmed.substring(0, colonIndex);
  const minutesPart = trimmed.substring(colonIndex + 1);
  
  let validHours = hoursPart.replace(/[^\d]/g, '');
  if (validHours.length > MAX_HHMM_HOURS_DIGITS) {
    validHours = validHours.substring(0, MAX_HHMM_HOURS_DIGITS);
  }
  
  // Validate minutes part (0-59, max 2 digits)
  let validMinutes = minutesPart.replace(/[^\d]/g, '');
  if (validMinutes.length > 2) {
    validMinutes = validMinutes.substring(0, 2);
  }
  
  // Check if minutes exceed 59
  const minutesNum = parseInt(validMinutes) || 0;
  if (minutesNum > 59) {
    validMinutes = '59';
  }
  
  // Build the validated value
  const validatedValue = validHours + ':' + validMinutes;
  
  // Determine error message
  let error = '';
  if (hoursPart !== validHours && hoursPart.length > 0) {
    error = `Hours must be digits only (max ${MAX_HHMM_HOURS_DIGITS} digits)`;
  } else if (minutesPart !== validMinutes && minutesPart.length > 0) {
    error = 'Minutes must be 0-59 (2 digits max)';
  } else if (minutesNum > 59 && minutesPart.length > 0) {
    error = 'Minutes cannot exceed 59';
  }
  
  return { value: validatedValue, error };
};

// Helper function to format Hours value - ensures colon is present, adds ":00" if missing
export const formatHoursValue = (value) => {
  if (!value || value.trim() === '') return value;
  
  const trimmed = value.trim();
  
  // If value already contains a colon, validate and return
  if (trimmed.includes(':')) {
    const validation = validateHoursInput(trimmed);
    return validation.value;
  }
  
  // If value is just digits, append ":00"
  if (/^\d+$/.test(trimmed)) {
    const hours =
      trimmed.length > MAX_HHMM_HOURS_DIGITS
        ? trimmed.substring(0, MAX_HHMM_HOURS_DIGITS)
        : trimmed;
    return `${hours}:00`;
  }
  
  // Otherwise validate and return
  const validation = validateHoursInput(trimmed);
  return validation.value;
};
